import React, { useState, useEffect } from 'react';
import DebugRender from 'components/Debug/Render';
import { useSelector } from 'react-redux';
import { RootState } from 'types/state';
import { rootSelectors } from '@redux/modules/selectors';
import i18n from 'util/i18n';
import { FormErrorsUtil } from 'helpers/formErrors';
import { FormStateSubsectionsKeys } from '@redux/modules/form/types';
import { ff } from 'helpers/featureFlags';
import Show from 'components/Form/Show';
import { getFriendlySelector } from 'components/Anchor';
import { ErrorListMessages } from './types';
import { getErrorMessages, getQuestion, handleListHeaderTemplate } from './utility';

/**
 * In the scenario the accordian is closed, this will look for a specific
 * property and trigger an event to toggle the accordian open. This is to
 * support EN-4499.
 *
 * When we can move away from "query DOM element" approach, we can update the
 * below logic and clean it up. Ideally, we want to trigger a state update and
 * use that state update to trigger the accordion open/close.
 */
const onAnchorClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
  const anchorHash = event.currentTarget.hash;
  const targetAnchorElement = document.querySelector(anchorHash);

  if (targetAnchorElement) {
    if (getComputedStyle(targetAnchorElement).visibility === 'collapse') {
      // We apply 'disable-animations' to the body and use CSS to disable
      // animations. We need to disable animations to provide a smooth
      // transition from the top to the correct section within the accordian.
      // Due to animations, there is a delay until it is fully opened. Having
      // the user wait while the accordian is still opening off screen isn't a
      // good experience.
      document.querySelector('body')?.classList.add('disable-animations'); // eslint-disable-line no-unused-expressions

      // This triggers the onClick event associated to the accordian
      // "open/close" element.
      // eslint-disable-next-line no-unused-expressions
      targetAnchorElement
        .closest('.item')
        ?.querySelector<HTMLDivElement>('.summary .left a, .summary a.left')
        ?.click();

      // The delay reverts the disable-animations toggle.
      setTimeout(() => {
        document.querySelector('body')?.classList.remove('disable-animations'); // eslint-disable-line no-unused-expressions
      }, 1000);
    }
  }
};

/**
 * ErrorList currently queries the DOM and looks for errors associated to
 * sections/subsections, using "data-section={section}" and
 * "data-subsection={subsection}".
 */
export const ErrorList: React.FC = () => {
  const [state, setState] = useState<ErrorListMessages>({
    ...getErrorMessages(),
  });
  const formErrors = useSelector((state: RootState) => rootSelectors.form.getSectionErrors(state, state.section.section.name ?? '')); // eslint-disable-line no-shadow
  const isFeatureFlag5301Active = ff(
    useSelector((state: RootState) => state.featureFlags)// eslint-disable-line no-shadow
  ).getActive('EN-5301');

  // ErrorList queries the page for errors. At this time, we know this isn't a
  // good design, however, refactoring away from this is not easy to do as the
  // source of truth for error handling is only available from
  // state.application. State.application only generates errors when the
  // components are on the screen. With that being said, the MutationObserver
  // will subscribe to the page and look for changes in the DOM. When the DOM
  // changes, this should trigger and update the state. By doing this, this
  // fixes the following:
  // * Once errors are removed from a component, the ErrorList automatically
  //   updates. We shouldn't have any disconnect between the two.
  // * In the scenario a component updates "Errors" after a delayed period,
  //   ErrorList will automatically update to pick up that error.
  //
  // However, there are some cons with this approach:
  // * Some bugs can be impact performance & memory
  // * The ErrorList is pigeoned hard into the current design, creating blockers
  //   for "Review" redesigns. This makes it more difficult to optimize
  //   performance.
  const [observer, setObserver] = useState<MutationObserver>();
  const [isObserverSubscribed, setObserverSubscribed] = useState<boolean>(false);
  const { count, messages } = state;
  const updateState = () => setState({ ...getErrorMessages() });

  useEffect(() => {
    if (observer === undefined && isFeatureFlag5301Active === false) {
      setObserver(new MutationObserver(updateState));
      updateState();
    }
  }, []);

  useEffect(() => { // eslint-disable-line consistent-return
    if (
      observer !== undefined
      && isObserverSubscribed === false
      && isFeatureFlag5301Active === false
    ) {
      const targetElement = document.getElementById('main-content');

      if (targetElement) {
        observer.observe(targetElement, {
          attributes: false,
          childList: true,
          subtree: true,
        });

        setObserverSubscribed(true);

        // When the mount is unmounted, it'll call the cleanup and disconnect the observer.
        return function cleanup() {
          observer.disconnect();
          setObserverSubscribed(false);
        };
      }
    }
  }, [observer]);

  if (isFeatureFlag5301Active === true) { return <NewErrorList />; }

  // If there were no issues counted then return nothing.
  if (count === 0) { return null; }

  return (
    <ErrorListContainer hideContainer={false} count={count}>
      {Object.keys(messages).map((key, index) => {
        const item = messages[key];

        return (
          <span key={item[0].title ?? index}>
            <h4>{item[0].title}</h4>
            <ul>
              {item.map((msg, i) => (
                <li key={i}>
                  <a href={`#${msg.id}`} title={msg.message ?? ''} onClick={onAnchorClick}>
                    {msg.message}
                  </a>
                </li>
              ))}
            </ul>
          </span>
        );
      })}

      <Show when={formErrors.length > 0 && count === 0}>
        <h3>Additional Errors found</h3>

        <NewErrorList hideContainer />
      </Show>
    </ErrorListContainer>
  );
};

export default ErrorList;


interface NewErrorListProps {
  /**
   * HideContainer will show/hide the outer wrapper of the content. This is the
   * pretty error box.
   *
   * Default: false
   */
  hideContainer?: boolean
}

/**
 * NewErrorList is a component that will replace ErrorList.
 */
export const NewErrorList: React.FC<NewErrorListProps> = ({ hideContainer }) => {
  const section = useSelector((state: RootState) => state.section.section.name ?? '');
  const sectionErrors = useSelector((state: RootState) => rootSelectors.form.getSectionErrors(state, section));
  const formErrors = sectionErrors.map((e) => ({
    ...e,
    errors: FormErrorsUtil(e.errors)
      .reduceRedundantErrors()
      .reduceDuplicateErrorsRelatedToGrouping()
      .groupByListItem(),
  }));
  const count = formErrors.map((e) => e.errors.formErrors.length).reduce((a, b) => a + b, 0);

  return (
    <ErrorListContainer count={count} hideContainer={hideContainer ?? false}>
      {formErrors.map((item, index) => (
        <span key={index}>
          <h4>{item.label}</h4>
          <ul>
            {item.errors.root.map((msg, i) => (
              <ErrorListItem
                key={`ErrorListItem.${item.key}.${msg.attribute}.${msg.code}`}
                {...msg}
                section={item.key ?? ''}
              />
            ))}
          </ul>
          {item.errors.groups.map((errorItem, i) => (
            <React.Fragment key={`ErrorListGroup.${item.key}.${i}`}>
              <ErrorListItemHeader
                index={i}
                subsectionKey={item.key ?? ''}
                listHeader={item.listHeader}
                listKey={item.listKey}
              />
              <ul>
                {errorItem.map((msg) => (
                  <ErrorListItem
                    key={`ErrorListItem.${item.key}.${msg.attribute}.${msg.code}`}
                    {...msg}
                    section={item.key ?? ''}
                  />
                ))}
              </ul>
            </React.Fragment>
          ))}
        </span>
      ))}
    </ErrorListContainer>
  );
};

interface ErrorListItemHeaderProps {
  index: number
  subsectionKey: string
  listHeader?: string
  listKey?: string
}

const ErrorListItemHeader: React.FC<ErrorListItemHeaderProps> = ({
  index,
  subsectionKey,
  listHeader,
  listKey = 'List',
}) => {
  const item = useSelector(
    (state: RootState) =>
      // We use 'any' here as trying to type-safe is a bit challenging, as we
      // need a lot of dynamic keys and support (type) instances where a key
      // doesn't exist. However, optional chaining allows this to be undefined
      // when those conditions aren't met.
      (state.form[subsectionKey as FormStateSubsectionsKeys]?.data as any)?.[listKey]?.items?.[// eslint-disable-line implicit-arrow-linebreak
        index
      ]
  );
  const text = handleListHeaderTemplate(index, item, listHeader);

  return <h5>{text}</h5>;
};

interface ErrorListItemProps {
  code: string
  attribute: string
  section: string
}

const ErrorListItem: React.FC<ErrorListItemProps> = ({ code, attribute, section }) => {
  const title = code.includes('error.') ? i18n.t(`${code}.title`) : code;
  const question = getQuestion(attribute);

  return (
    <li>
      <a
        href={getFriendlySelector(`#input--${section.replace(/_/g, '-')}.${attribute}`)}
        onClick={onAnchorClick}
      >
        {question} - {title}
      </a>
    </li>
  );
};

interface ErrorListContainerProps {
  count: number
  hideContainer: boolean
}

const ErrorListContainer: React.FC<ErrorListContainerProps> = ({
  children,
  count,
  hideContainer,
}) => {
  if (hideContainer === true) { return <>{children}</>; }
  if (count === 0) { return null; }

  return (
    <div className="error-list">
      <div className="usa-alert usa-alert-error" role="alert">
        <div className="usa-alert-body">
          <DebugRender parent={null} />

          <h3 className="usa-alert-heading">
            Here is a list of the {count} {count > 1 ? 'questions' : 'question'}
            {' with issues'}
          </h3>

          {children}
        </div>
      </div>
    </div>
  );
};

export const internalTesting = {
  ErrorListContainer,
  ErrorListItemHeader,
};
